import { Fragment } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  Typography,
} from '@eye-able/ui';
import type {
  PolicyMatchMode,
  PolicyResponse,
  PolicyRuleGroup,
} from '@http/queries/useCreatePolicy';
import { getRuleFieldLabel, getRuleOperatorLabel } from './PolicyRuleLabels';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { getPriorityTranslationKey } from '../priority';
import { useDomainOptions } from '@/components/FormDomainSelect';
import { useFetchTeams } from '@http/queries';
import { userAtom } from '@/global-store';
import { useAtomValue } from 'jotai';

function MatchModeConnector({ matchMode }: { matchMode: PolicyMatchMode }) {
  const { t } = useInlineTranslation();
  return (
    <div className='eye:flex eye:items-center eye:gap-xs'>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
      <Typography variant='body-s' className='eye:text-grey-120'>
        {matchMode === 'all' ? t('and') : t('or')}
      </Typography>
      <span className='eye:h-px eye:flex-1 eye:bg-grey-20' />
    </div>
  );
}

const scopeNounKeys: Record<string, string> = {
  image: 'nounImage',
  'meta-tag': 'nounMetaTag',
  file: 'nounFile',
};

/** "Any image where…" header for scoped groups; page groups need none. */
function RuleGroupBlock({ group }: { group: PolicyRuleGroup }) {
  const { t } = useInlineTranslation();
  const heading =
    group.scope === 'page'
      ? null
      : t(group.quantifier === 'every' ? 'everyScopeWhere' : 'anyScopeWhere', {
          scope: t(scopeNounKeys[group.scope] ?? group.scope),
        });
  return (
    <div className='eye:flex eye:flex-col eye:gap-xs eye:rounded-8 eye:border eye:border-grey-20 eye:p-xs'>
      {heading ? (
        <Typography variant='body-s' className='eye:text-grey-120'>
          {heading}
        </Typography>
      ) : null}
      {group.rules.map((rule, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <MatchModeConnector matchMode={group.matchMode} />
          ) : null}
          <div className='eye:flex eye:flex-wrap eye:items-baseline eye:gap-4xs'>
            <Typography variant='body-s'>
              {t(getRuleFieldLabel(rule.field))}
            </Typography>
            <Typography className='eye:text-grey-120'>
              {t(getRuleOperatorLabel(rule.operator)).toLowerCase()}
            </Typography>
            <Typography className='eye:break-all'>
              &ldquo;{rule.value}&rdquo;
            </Typography>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

export function PolicyRuleDialog({
  policy,
  onOpenChange,
}: {
  policy: PolicyResponse | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useInlineTranslation();
  const user = useAtomValue(userAtom);
  const { data: teams = [] } = useFetchTeams(user?.primaryOrganisation);
  const domainOptions = useDomainOptions(policy?.auditEntryIds ?? []);
  const team = teams.find((candidate) => candidate.id === policy?.teamId);
  const selectedDomains = policy
    ? policy.auditEntryIds
        .map((id) => domainOptions.find((domain) => domain.id === id))
        .filter((domain): domain is (typeof domainOptions)[number] =>
          Boolean(domain),
        )
    : [];

  return (
    <Dialog open={policy !== null} onOpenChange={onOpenChange}>
      <DialogContent maxWidth='md' className='eye:w-full'>
        <DialogTitle>{t('policyDetails')}</DialogTitle>
        {policy ? (
          <>
            <div className='eye:flex eye:flex-col eye:gap-s'>
              <div className='eye:grid eye:gap-xs eye:sm:grid-cols-2'>
                <DetailField label={t('policyName')} value={policy.name} />
                <DetailField
                  label={t('priority')}
                  value={t(getPriorityTranslationKey(policy.priority))}
                />
                {policy.description ? (
                  <DetailField
                    label={t('description')}
                    value={policy.description}
                    className='eye:sm:col-span-2'
                  />
                ) : null}
                <DetailField
                  label={t('teamOptional')}
                  value={team?.name ?? t('noTeam')}
                />
                <DetailField
                  label={t('domainScope')}
                  value={
                    policy.domainScope === 'all-domains'
                      ? t('allDomainsInAccount')
                      : t('specificDomainsOnly')
                  }
                />
              </div>

              {policy.domainScope === 'selected-domains' ? (
                <DetailField
                  label={t('includedDomains')}
                  value={
                    selectedDomains.length
                      ? selectedDomains.map((domain) => domain.label).join(', ')
                      : policy.auditEntryIds.join(', ')
                  }
                />
              ) : null}

              <div className='eye:flex eye:flex-col eye:gap-xs'>
                <Typography variant='body-s-highlight'>
                  {t('issueRule')}
                </Typography>
                {policy.rule.groups.map((group, index) => (
                  <Fragment key={index}>
                    {index > 0 ? (
                      <MatchModeConnector matchMode={policy.rule.matchMode} />
                    ) : null}
                    <RuleGroupBlock group={group} />
                  </Fragment>
                ))}
              </div>
            </div>
          </>
        ) : null}
        <DialogActions>
          <DialogClose asChild>
            <Button type='button' variant='outline'>
              {t('close')}
            </Button>
          </DialogClose>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

function DetailField({
  label,
  value,
  className,
}: {
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`eye:flex eye:flex-col eye:gap-5xs ${className ?? ''}`}>
      <Typography variant='body-s' className='eye:text-grey-120'>
        {label}
      </Typography>
      <Typography className='eye:break-words'>{value}</Typography>
    </div>
  );
}

export default PolicyRuleDialog;
